// Step 2: Add/remove line items (variant search, qty, unit cost, serial entry).
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { SerialScanInput } from "./serial-scan-input";
import { SerialXLSXUpload } from "./serial-xlsx-upload";

interface VariantResult {
  id: string;
  sku: string;
  productName: string;
  costPrice: string;
}

interface ImportItem {
  id: string;
  variantId: string;
  variantSku: string | null;
  productName: string | null;
  quantity: number;
  unitCost: string;
  serials: string[];
}

interface ItemsResponse {
  data: { import: unknown; items: ImportItem[] };
}

interface Props {
  importId: string;
}

export function ImportFormStepTwo({ importId }: Props) {
  const qc = useQueryClient();

  // Items already saved on the import
  const { data: detail } = useQuery({
    queryKey: ["import-detail", importId],
    queryFn: () =>
      apiClient
        .get<ItemsResponse>(`/v1/imports/${importId}`)
        .then((r) => r.data.data),
  });
  const items: ImportItem[] = (detail?.items as ImportItem[]) ?? [];

  // New-row state
  const [variantSearch, setVariantSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selectedVariant, setSelectedVariant] = useState<VariantResult | null>(null);
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [serials, setSerials] = useState<string[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(variantSearch), 300);
    return () => clearTimeout(t);
  }, [variantSearch]);

  const { data: variantResults = [] } = useQuery({
    queryKey: ["variant-search", debouncedQ],
    queryFn: () =>
      apiClient
        .get<{ data: VariantResult[] }>("/v1/variants/search", { params: { q: debouncedQ } })
        .then((r) => r.data.data),
    enabled: debouncedQ.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (body: object) =>
      apiClient.post(`/v1/imports/${importId}/items`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      setSelectedVariant(null);
      setVariantSearch("");
      setQty("1");
      setUnitCost("");
      setSerials([]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.delete(`/v1/imports/${importId}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-detail", importId] }),
  });

  function handleAdd() {
    if (!selectedVariant) return;
    addMutation.mutate({
      variantId: selectedVariant.id,
      quantity: parseInt(qty, 10) || 1,
      unitCost: unitCost || "0",
      serials,
    });
  }

  return (
    <div className="space-y-6">
      {/* Existing items */}
      {items.length > 0 && (
        <Table>
          <THead>
            <Tr>
              <Th>SKU</Th>
              <Th>Sản phẩm</Th>
              <Th className="text-right">SL</Th>
              <Th className="text-right">Đơn giá</Th>
              <Th className="text-right">Serials</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {items.map((item) => (
              <Tr key={item.id}>
                <Td className="font-mono text-xs">{item.variantSku ?? "—"}</Td>
                <Td>{item.productName ?? "—"}</Td>
                <Td className="text-right">{item.quantity}</Td>
                <Td className="text-right">{item.unitCost}</Td>
                <Td className="text-right">{item.serials?.length ?? 0}</Td>
                <Td>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => deleteMutation.mutate(item.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {/* Add new row */}
      <div className="rounded-md border p-4 space-y-3 bg-muted/30">
        <p className="text-sm font-medium">Thêm mặt hàng</p>

        <div className="space-y-1.5">
          <Label>Tìm variant (SKU / tên)</Label>
          <Input
            placeholder="Gõ ít nhất 2 ký tự…"
            value={selectedVariant ? `${selectedVariant.sku} — ${selectedVariant.productName}` : variantSearch}
            onChange={(e) => {
              setSelectedVariant(null);
              setVariantSearch(e.target.value);
            }}
          />
          {!selectedVariant && variantResults.length > 0 && (
            <ul className="rounded-md border bg-background shadow-sm divide-y text-sm max-h-40 overflow-auto">
              {variantResults.map((v) => (
                <li
                  key={v.id}
                  className="px-3 py-2 cursor-pointer hover:bg-accent"
                  onClick={() => {
                    setSelectedVariant(v);
                    setUnitCost(v.costPrice);
                  }}
                >
                  <span className="font-mono mr-2">{v.sku}</span>
                  {v.productName}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-3">
          <div className="space-y-1 flex-1">
            <Label>Số lượng</Label>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div className="space-y-1 flex-1">
            <Label>Đơn giá nhập</Label>
            <Input type="number" min="0" placeholder="0" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
          </div>
        </div>

        <SerialScanInput
          value={serials}
          onChange={setSerials}
          expectedCount={parseInt(qty, 10) || 0}
        />

        <Button
          onClick={handleAdd}
          disabled={!selectedVariant || addMutation.isPending}
          size="sm"
        >
          {addMutation.isPending ? "Đang thêm…" : "Thêm vào phiếu"}
        </Button>
      </div>

      {/* Bulk upload — file may cover one or many variants (BE detects by header). */}
      <SerialXLSXUpload
        importId={importId}
        variant={
          selectedVariant
            ? { id: selectedVariant.id, sku: selectedVariant.sku, productName: selectedVariant.productName }
            : null
        }
        unitCost={unitCost}
      />
    </div>
  );
}
